import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Checkbox } from '../../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Search, Eye, Lock, Unlock, Users, UserCheck, UserX, Building, User, CreditCard, Briefcase, ChevronDown, ChevronRight, Upload, Download, FileText, MapPin, Shield, FileBarChart, Sparkles, AlertTriangle } from 'lucide-react';
import axios from 'axios';
import { toast } from '../../hooks/use-toast';
import { useNavigate } from 'react-router-dom';
import TablePagination from '../../components/TablePagination';
import GenericBulkActionsBar from '../../components/GenericBulkActionsBar';
import CSVImportModal from '../../components/CSVImportModal';
import ClientStatementDialog from '../../components/admin/ClientStatementDialog';
import ClientWaveRecapDialog from '../../components/admin/ClientWaveRecapDialog';
import ClientContactsDialog from '../../components/admin/ClientContactsDialog';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ClientsManagement = () => {
  const [clients, setClients] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [accountTypeFilter, setAccountTypeFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sectorFilter, setSectorFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 15;

  const [selectedClients, setSelectedClients] = useState([]);
  const [selectAll, setSelectAll] = useState(false);

  // Expanded companies
  const [expandedCompanies, setExpandedCompanies] = useState(new Set());

  // CSV Import
  const [showImportModal, setShowImportModal] = useState(false);

  // Payment policy dialog
  const [policyDialogOpen, setPolicyDialogOpen] = useState(false);
  const [policyDialogTarget, setPolicyDialogTarget] = useState(null); // {client, companyName}
  const [policies, setPolicies] = useState([]);
  const [selectedPolicyId, setSelectedPolicyId] = useState('');
  const [savingPolicy, setSavingPolicy] = useState(false);

  // Sector dialog
  const [sectorDialogOpen, setSectorDialogOpen] = useState(false);
  const [sectorDialogTarget, setSectorDialogTarget] = useState(null);
  const [sectors, setSectors] = useState([]);
  const [selectedSectorCode, setSelectedSectorCode] = useState('');
  const [customSectorText, setCustomSectorText] = useState('');
  const [savingSector, setSavingSector] = useState(false);

  // Client role dialog
  const [clientRoleDialogOpen, setClientRoleDialogOpen] = useState(false);
  const [clientRoleTarget, setClientRoleTarget] = useState(null);
  const [selectedClientRole, setSelectedClientRole] = useState('');
  const [savingClientRole, setSavingClientRole] = useState(false);

  const CLIENT_ROLE_OPTIONS = [
    { value: '', label: 'Aucun (accès complet)' },
    { value: 'client_admin', label: 'Administrateur' },
    { value: 'client_finance', label: 'Finance' },
    { value: 'client_logistique', label: 'Logistique' },
    { value: 'client_auditor', label: 'Auditeur (lecture seule)' },
  ];

  // Company info dialog
  const [companyInfoDialogOpen, setCompanyInfoDialogOpen] = useState(false);
  const [companyInfoTarget, setCompanyInfoTarget] = useState(null);
  const [companyInfoForm, setCompanyInfoForm] = useState({ rccm: '', compteContribuable: '', address: '' });

  // Statement dialog
  const [statementDialogOpen, setStatementDialogOpen] = useState(false);  const [statementTarget, setStatementTarget] = useState(null);

  const openStatementDialog = (client) => {
    setStatementTarget(client);
    setStatementDialogOpen(true);
  };

  // Wave Recap dialog (PREMIUM — livrable facturable)
  const [waveRecapDialogOpen, setWaveRecapDialogOpen] = useState(false);
  const [waveRecapTarget, setWaveRecapTarget] = useState(null);
  const openWaveRecapDialog = (client) => {
    setWaveRecapTarget(client);
    setWaveRecapDialogOpen(true);
  };

  // Contacts dialog (multi-personas)
  const [contactsDialogOpen, setContactsDialogOpen] = useState(false);
  const [contactsTarget, setContactsTarget] = useState(null);
  const openContactsDialog = (client) => {
    setContactsTarget(client);
    setContactsDialogOpen(true);
  };

  const [savingCompanyInfo, setSavingCompanyInfo] = useState(false);

  // Contacts stats (map client_id → { total, buckets_covered })
  const [contactsStats, setContactsStats] = useState({});

  useEffect(() => { fetchClients(); fetchContactsStats(); }, []);

  // Group clients by company and build display rows
  const { groupedRows, filteredCount, stats, sectorOptions } = useMemo(() => {
    let filtered = clients;

    if (accountTypeFilter !== 'all') {
      filtered = filtered.filter(c => c.accountType === accountTypeFilter);
    }
    if (statusFilter !== 'all') {
      const isActive = statusFilter === 'active';
      filtered = filtered.filter(c => c.isActive === isActive);
    }
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(c =>
        c.firstName?.toLowerCase().includes(query) ||
        c.lastName?.toLowerCase().includes(query) ||
        c.email?.toLowerCase().includes(query) ||
        c.companyName?.toLowerCase().includes(query) ||
        c.clientNumber?.toLowerCase().includes(query) ||
        c.sector_name?.toLowerCase().includes(query)
      );
    }
    if (sectorFilter !== 'all') {
      filtered = filtered.filter(c => c.sector_code === sectorFilter);
    }

    // Group by company
    const companyMap = {};
    const particuliers = [];
    filtered.forEach(c => {
      if (c.accountType === 'entreprise' && c.companyName) {
        if (!companyMap[c.companyName]) companyMap[c.companyName] = [];
        companyMap[c.companyName].push(c);
      } else {
        particuliers.push(c);
      }
    });

    // Build display rows: company header + members (if expanded) + particuliers
    const rows = [];
    const sortedCompanies = Object.keys(companyMap).sort();
    for (const companyName of sortedCompanies) {
      const members = companyMap[companyName];
      const representative = members[0]; // Use first member for company-level data
      rows.push({
        type: 'company',
        companyName,
        members,
        memberCount: members.length,
        sector_code: representative.sector_code,
        sector_name: representative.sector_name,
        custom_sector: representative.custom_sector,
        default_payment_policy_id: representative.default_payment_policy_id,
        isActive: members.some(m => m.isActive),
        representative,
      });
      if (expandedCompanies.has(companyName)) {
        members.forEach(m => rows.push({ type: 'member', client: m, companyName }));
      }
    }
    particuliers.forEach(c => rows.push({ type: 'individual', client: c }));

    // Sector filter options from all clients
    const sectorOpts = [...new Set(clients.filter(c => c.sector_code).map(c => c.sector_code))].map(code => {
      const cl = clients.find(c => c.sector_code === code);
      return { code, name: cl?.sector_name || code };
    });

    return {
      groupedRows: rows,
      filteredCount: filtered.length,
      stats: {
        total: clients.length,
        active: clients.filter(c => c.isActive).length,
        inactive: clients.filter(c => !c.isActive).length,
        particuliers: clients.filter(c => c.accountType === 'particulier').length,
        entreprises: [...new Set(clients.filter(c => c.accountType === 'entreprise' && c.companyName).map(c => c.companyName))].length,
      },
      sectorOptions: sectorOpts,
    };
  }, [clients, searchQuery, accountTypeFilter, statusFilter, sectorFilter, expandedCompanies]);

  // Paginated rows
  const paginatedRows = useMemo(() => {
    return groupedRows.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);
  }, [groupedRows, currentPage]);

  useEffect(() => { setCurrentPage(1); }, [searchQuery, accountTypeFilter, statusFilter, sectorFilter]);

  const toggleCompany = (companyName) => {
    setExpandedCompanies(prev => {
      const next = new Set(prev);
      if (next.has(companyName)) next.delete(companyName);
      else next.add(companyName);
      return next;
    });
  };

  const fetchClients = async () => {
    try {
      const response = await axios.get(`${API}/admin/users?role=client&pageSize=100`, {
        headers: { 'Authorization': 'Bearer cookie-auth' }
      });
      const usersArray = response.data.users || response.data || [];
      const clientsOnly = usersArray.filter(u => u.role === 'client');
      setClients(clientsOnly);
      setLoading(false);
    } catch (error) {
      toast({ title: "Erreur", description: "Impossible de charger les clients", variant: "destructive" });
      setLoading(false);
    }
  };

  const fetchContactsStats = async () => {
    try {
      const res = await axios.get(`${API}/clients/contacts-stats`);
      setContactsStats(res.data?.stats || {});
    } catch (e) {
      console.error('Error fetching contacts stats:', e);
    }
  };

  const handleContactsDialogClose = () => {
    setContactsDialogOpen(false);
    // Refresh stats after admin closes the dialog (they may have added/removed contacts)
    fetchContactsStats();
  };

  const fetchPolicies = async () => {
    try {
      const res = await axios.get(`${API}/payment-policies/`, {});
      setPolicies(res.data);
    } catch (e) { console.error('Error fetching policies:', e); }
  };

  const fetchSectors = async () => {
    try {
      const res = await axios.get(`${API}/admin/business-sectors`, {});
      setSectors(res.data);
    } catch (e) { console.error('Error fetching sectors:', e); }
  };

  // --- Sector dialog ---
  const openSectorDialog = (client) => {
    setSectorDialogTarget({ client, companyName: client.companyName });
    setSelectedSectorCode(client.sector_code || '');
    setCustomSectorText(client.custom_sector || '');
    if (sectors.length === 0) fetchSectors();
    setSectorDialogOpen(true);
  };

  const saveSectorForClient = async () => {
    if (!sectorDialogTarget) return;
    setSavingSector(true);
    try {
      const res = await axios.patch(
        `${API}/admin/users/${sectorDialogTarget.client.id}/sector`,
        {
          sector_code: selectedSectorCode || null,
          custom_sector: selectedSectorCode === 'AUTRE' ? customSectorText : null
        },
        {}
      );
      const data = res.data;
      const label = data.company_name || sectorDialogTarget.client.firstName;
      const countMsg = data.updated_count > 1 ? ` (${data.updated_count} utilisateurs mis a jour)` : '';
      toast({
        title: 'Secteur mis a jour',
        description: `${label}${countMsg}`,
      });
      setSectorDialogOpen(false);
      fetchClients();
    } catch (e) {
      toast({ title: 'Erreur', description: e.response?.data?.detail || 'Erreur', variant: 'destructive' });
    } finally {
      setSavingSector(false);
    }
  };

  // --- Company Info dialog ---
  const openCompanyInfoDialog = (client) => {
    setCompanyInfoTarget(client);
    setCompanyInfoForm({
      rccm: client.rccm || '',
      compteContribuable: client.compteContribuable || '',
      address: client.address || ''
    });
    setCompanyInfoDialogOpen(true);
  };

  const saveCompanyInfo = async () => {
    if (!companyInfoTarget) return;
    setSavingCompanyInfo(true);
    try {
      const res = await axios.patch(
        `${API}/admin/users/${companyInfoTarget.id}/company-info`,
        companyInfoForm,
        {}
      );
      toast({
        title: 'Infos entreprise mises a jour',
        description: res.data?.message || 'Enregistre',
      });
      setCompanyInfoDialogOpen(false);
      fetchClients();
    } catch (e) {
      toast({ title: 'Erreur', description: e.response?.data?.detail || 'Erreur', variant: 'destructive' });
    } finally {
      setSavingCompanyInfo(false);
    }
  };

  // --- Payment policy dialog ---
  const openPolicyDialog = (client) => {
    setPolicyDialogTarget({ client, companyName: client.companyName });
    setSelectedPolicyId(client.default_payment_policy_id || '');
    if (policies.length === 0) fetchPolicies();
    setPolicyDialogOpen(true);
  };

  // --- Client Role dialog ---
  const openClientRoleDialog = (client) => {
    setClientRoleTarget(client);
    setSelectedClientRole(client.client_role || '');
    setClientRoleDialogOpen(true);
  };

  const saveClientRole = async () => {
    if (!clientRoleTarget) return;
    setSavingClientRole(true);
    try {
      await axios.patch(
        `${API}/admin/users/${clientRoleTarget.id}/client-role`,
        { client_role: selectedClientRole || null },
        {}
      );
      toast({ title: 'Sous-rôle mis à jour', description: `${clientRoleTarget.firstName} ${clientRoleTarget.lastName}` });
      setClientRoleDialogOpen(false);
      fetchClients();
    } catch (e) {
      toast({ title: 'Erreur', description: e.response?.data?.detail || 'Erreur', variant: 'destructive' });
    } finally {
      setSavingClientRole(false);
    }
  };

  const savePolicyForClient = async () => {
    if (!policyDialogTarget) return;
    setSavingPolicy(true);
    try {
      const res = await axios.patch(
        `${API}/admin/users/${policyDialogTarget.client.id}/payment-policy`,
        { default_payment_policy_id: selectedPolicyId || null },
        {}
      );
      const data = res.data;
      const label = data.company_name || policyDialogTarget.client.firstName;
      const policyName = policies.find(p => p.id === selectedPolicyId)?.name || 'Aucune (défaut système)';
      const countMsg = data.updated_count > 1 ? ` (${data.updated_count} utilisateurs)` : '';
      toast({
        title: 'Condition de paiement mise a jour',
        description: `${label} : ${policyName}${countMsg}`,
      });
      setPolicyDialogOpen(false);
      fetchClients();
    } catch (e) {
      toast({ title: 'Erreur', description: e.response?.data?.detail || 'Erreur', variant: 'destructive' });
    } finally {
      setSavingPolicy(false);
    }
  };

  const toggleClientStatus = async (clientId, currentStatus) => {
    try {
      await axios.patch(`${API}/admin/users/${clientId}/toggle-status`);
      toast({
        title: currentStatus ? "Client desactive" : "Client active",
        description: `Le compte client a ete ${currentStatus ? 'desactive' : 'active'} avec succes`
      });
      fetchClients();
    } catch (error) {
      toast({ title: "Erreur", description: "Impossible de modifier le statut", variant: "destructive" });
    }
  };

  // Bulk selection
  const handleSelectClient = (clientId) => {
    setSelectedClients(prev =>
      prev.includes(clientId) ? prev.filter(id => id !== clientId) : [...prev, clientId]
    );
  };

  const handleSelectAll = () => {
    const allIds = clients.map(c => c.id);
    if (selectAll) { setSelectedClients([]); setSelectAll(false); }
    else { setSelectedClients(allIds); setSelectAll(true); }
  };

  const handleBulkActivate = async () => {
    try {
      const response = await axios.post(`${API}/bulk-actions/clients/activate`, { clientIds: selectedClients });
      toast({ title: "Clients actives", description: response.data.message });
      setSelectedClients([]); setSelectAll(false); fetchClients();
    } catch (error) {
      toast({ title: "Erreur", description: error.response?.data?.detail || "Erreur", variant: "destructive" });
    }
  };

  const handleBulkDeactivate = async () => {
    if (!window.confirm(`Desactiver ${selectedClients.length} client(s) ?`)) return;
    try {
      const response = await axios.post(`${API}/bulk-actions/clients/deactivate`, { clientIds: selectedClients });
      toast({ title: "Clients desactives", description: response.data.message });
      setSelectedClients([]); setSelectAll(false); fetchClients();
    } catch (error) {
      toast({ title: "Erreur", description: error.response?.data?.detail || "Erreur", variant: "destructive" });
    }
  };

  const handleBulkExport = async () => {
    try {
      const response = await axios.post(`${API}/bulk-actions/clients/export`, { clientIds: selectedClients }, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a'); link.href = url;
      link.setAttribute('download', `clients_export_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link); link.click(); link.remove();
      toast({ title: "Export reussi", description: `${selectedClients.length} client(s) exporte(s)` });
    } catch (error) {
      toast({ title: "Erreur", description: "Erreur lors de l'export", variant: "destructive" });
    }
  };

  // Sector/Payment badge renderers
  // Badge "Configuration recommandée" — pour clients avec un seul contact (anti-spam WA)
  const renderContactsHealthBadge = (client) => {
    if (!client?.id) return null;
    const stat = contactsStats[client.id];
    const total = stat?.total ?? 0;
    // total 0 → aucun contact seedé (edge case), total 1 → un seul contact reçoit tout
    if (total > 1) return null;
    const label = total === 0 ? 'Aucun contact' : 'Configuration recommandée';
    return (
      <button
        onClick={(e) => { e.stopPropagation(); openContactsDialog(client); }}
        data-testid={`contacts-health-badge-${client.id}`}
        title="Ce client n'a qu'un seul contact. Configurer plusieurs personas (CEO/Ops/Compta) pour éviter le sur-notifiement."
        className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full bg-amber-50 border border-amber-200 text-amber-700 hover:bg-amber-100 hover:border-amber-400 transition-colors leading-none"
      >
        <AlertTriangle className="w-3 h-3 shrink-0" />
        <span className="font-medium">{label}</span>
      </button>
    );
  };

  const renderSectorBadge = (row) => {
    const sectorCode = row.sector_code || row.client?.sector_code;
    const sectorName = row.sector_name || row.client?.sector_name;
    const customSector = row.custom_sector || row.client?.custom_sector;
    const client = row.representative || row.client;
    const label = sectorCode === 'AUTRE' ? (customSector || 'Autre') : (sectorName || 'Non defini');
    return (
      <button
        onClick={(e) => { e.stopPropagation(); openSectorDialog(client); }}
        data-testid={`sector-btn-${client?.id}`}
        className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-md border border-gray-200 hover:border-blue-400 hover:bg-blue-50 transition-colors max-w-[160px]"
      >
        <Briefcase className="w-3 h-3 text-gray-400 shrink-0" />
        <span className={`truncate ${sectorCode ? 'text-blue-700 font-medium' : 'text-gray-400'}`}>
          {label}
        </span>
      </button>
    );
  };

  const renderPaymentBadge = (row) => {
    const policyId = row.default_payment_policy_id || row.client?.default_payment_policy_id;
    const client = row.representative || row.client;
    return (
      <button
        onClick={(e) => { e.stopPropagation(); openPolicyDialog(client); }}
        data-testid={`payment-policy-btn-${client?.id}`}
        className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-md border border-gray-200 hover:border-emerald-400 hover:bg-emerald-50 transition-colors"
      >
        <CreditCard className="w-3 h-3 text-gray-400" />
        <span className={policyId ? 'text-emerald-700 font-medium' : 'text-gray-400'}>
          {policyId ? 'Configure' : 'Defaut'}
        </span>
      </button>
    );
  };

  const renderClientRoleBadge = (client) => {
    const roleLabels = {
      client_admin: 'Admin',
      client_finance: 'Finance',
      client_logistique: 'Logistique',
      client_auditor: 'Auditeur',
    };
    const roleColors = {
      client_admin: 'text-purple-700 bg-purple-50 border-purple-200 hover:border-purple-400',
      client_finance: 'text-blue-700 bg-blue-50 border-blue-200 hover:border-blue-400',
      client_logistique: 'text-orange-700 bg-orange-50 border-orange-200 hover:border-orange-400',
      client_auditor: 'text-gray-700 bg-gray-50 border-gray-200 hover:border-gray-400',
    };
    const role = client?.client_role;
    return (
      <button
        onClick={(e) => { e.stopPropagation(); openClientRoleDialog(client); }}
        data-testid={`client-role-btn-${client?.id}`}
        className={`inline-flex items-center gap-1 text-xs px-2 py-1 rounded-md border transition-colors ${role ? roleColors[role] || '' : 'border-gray-200 hover:border-gray-400 hover:bg-gray-50'}`}
      >
        <Shield className="w-3 h-3 shrink-0" />
        <span className={role ? 'font-medium' : 'text-gray-400'}>{role ? roleLabels[role] : 'Non défini'}</span>
      </button>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#1db584]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl md:text-3xl font-bold text-gray-900" data-testid="clients-page-title">Gestion des Clients</h1>
          <p className="text-gray-500 mt-1">Gérer tous les clients de la plateforme</p>
        </div>
      </div>

      {/* Bandeau anti-spam : nombre de clients avec ≤ 1 contact */}
      {(() => {
        const toConfigure = clients.filter(c => (contactsStats[c.id]?.total ?? 0) <= 1).length;
        if (toConfigure === 0 || clients.length === 0) return null;
        return (
          <div
            data-testid="contacts-config-banner"
            className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 flex items-start gap-3"
          >
            <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
            <div className="text-sm">
              <p className="font-semibold text-amber-900">
                {toConfigure} client{toConfigure > 1 ? 's ont' : ' a'} un seul contact actif
              </p>
              <p className="text-amber-800 mt-0.5">
                Pour éviter le sur-notifiement WhatsApp, segmenter les personas (CEO / Ops / Compta) via le bouton
                <Users className="inline w-3.5 h-3.5 mx-1 -mt-0.5" /> sur chaque ligne.
              </p>
            </div>
          </div>
        );
      })()}

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        {[
          { icon: Users, label: 'Total Clients', value: stats.total, color: 'text-gray-500' },
          { icon: UserCheck, label: 'Actifs', value: stats.active, color: 'text-green-600' },
          { icon: UserX, label: 'Inactifs', value: stats.inactive, color: 'text-red-600' },
          { icon: User, label: 'Particuliers', value: stats.particuliers, color: 'text-blue-600' },
          { icon: Building, label: 'Entreprises', value: stats.entreprises, color: 'text-purple-600' },
        ].map(({ icon: Icon, label, value, color }) => (
          <Card key={label}>
            <CardContent className="pt-6">
              <div className="text-center">
                <Icon className={`w-7 h-7 mx-auto mb-1.5 ${color}`} />
                <p className="text-xs text-gray-500">{label}</p>
                <p className={`text-xl font-bold ${color}`}>{value}</p>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Card>
        <CardHeader>
          <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-3">
            <CardTitle>Liste des Clients ({filteredCount})</CardTitle>
            <div className="flex flex-wrap gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowImportModal(true)}
                data-testid="import-clients-btn"
              >
                <Upload className="w-4 h-4 mr-2" />
                Importer CSV
              </Button>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <Input
                  placeholder="Rechercher..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 w-[220px]"
                  data-testid="client-search-input"
                />
              </div>
              <Select value={accountTypeFilter} onValueChange={setAccountTypeFilter}>
                <SelectTrigger className="w-[160px]"><SelectValue placeholder="Type de compte" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous les types</SelectItem>
                  <SelectItem value="particulier">Particuliers</SelectItem>
                  <SelectItem value="entreprise">Entreprises</SelectItem>
                </SelectContent>
              </Select>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-[130px]"><SelectValue placeholder="Statut" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous</SelectItem>
                  <SelectItem value="active">Actifs</SelectItem>
                  <SelectItem value="inactive">Inactifs</SelectItem>
                </SelectContent>
              </Select>
              <Select value={sectorFilter} onValueChange={setSectorFilter}>
                <SelectTrigger className="w-[180px]" data-testid="sector-filter"><SelectValue placeholder="Secteur" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous les secteurs</SelectItem>
                  {sectorOptions.map(s => (
                    <SelectItem key={s.code} value={s.code}>{s.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-10"></TableHead>
                <TableHead>Client / Entreprise</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Téléphone</TableHead>
                <TableHead>Secteur</TableHead>
                <TableHead>Paiement</TableHead>
                <TableHead>Sous-rôle</TableHead>
                <TableHead>Statut</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {paginatedRows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} className="text-center py-8 text-gray-500">
                    Aucun client trouve
                  </TableCell>
                </TableRow>
              ) : (
                paginatedRows.map((row, idx) => {
                  if (row.type === 'company') {
                    const isExpanded = expandedCompanies.has(row.companyName);
                    return (
                      <TableRow
                        key={`company-${row.companyName}`}
                        className="bg-gray-50/80 hover:bg-gray-100/80 cursor-pointer border-b border-gray-200"
                        data-testid={`company-row-${row.companyName}`}
                        onClick={() => toggleCompany(row.companyName)}
                      >
                        <TableCell className="w-10">
                          {isExpanded ? <ChevronDown className="w-4 h-4 text-gray-500" /> : <ChevronRight className="w-4 h-4 text-gray-500" />}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Building className="w-4 h-4 text-purple-500 shrink-0" />
                            <div>
                              <div className="flex items-center gap-2 flex-wrap">
                                <p className="font-semibold text-gray-900">{row.companyName}</p>
                                {(() => {
                                  const toConfig = row.members.filter(m => (contactsStats[m.id]?.total ?? 0) <= 1).length;
                                  if (toConfig === 0) return null;
                                  return (
                                    <span
                                      data-testid={`company-config-badge-${row.companyName.replace(/\s+/g, '-')}`}
                                      title={`${toConfig} membre(s) sur ${row.memberCount} n'ont qu'un seul contact — configurer plusieurs personas (CEO/Ops/Compta) pour éviter le sur-notifiement WhatsApp.`}
                                      className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full bg-amber-50 border border-amber-200 text-amber-700 leading-none"
                                    >
                                      <AlertTriangle className="w-3 h-3 shrink-0" />
                                      <span className="font-medium">{toConfig}/{row.memberCount} à configurer</span>
                                    </span>
                                  );
                                })()}
                              </div>
                              <p className="text-xs text-gray-400">{row.memberCount} membre{row.memberCount > 1 ? 's' : ''}</p>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell className="text-xs text-gray-400">—</TableCell>
                        <TableCell className="text-xs text-gray-400">—</TableCell>
                        <TableCell onClick={(e) => e.stopPropagation()}>
                          {renderSectorBadge(row)}
                        </TableCell>
                        <TableCell onClick={(e) => e.stopPropagation()}>
                          {renderPaymentBadge(row)}
                        </TableCell>
                        <TableCell className="text-xs text-gray-400 italic">—</TableCell>
                        <TableCell>
                          <span className="inline-flex items-center gap-1.5 text-xs text-gray-600">
                            <span className={`w-2 h-2 rounded-full ${row.isActive ? 'bg-green-500' : 'bg-gray-400'}`} />
                            {row.members.filter(m => m.isActive).length}/{row.memberCount} actif{row.members.filter(m => m.isActive).length > 1 ? 's' : ''}
                          </span>
                        </TableCell>
                        <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                          <div className="flex justify-end gap-1">
                            <Button
                              variant="ghost" size="icon"
                              onClick={() => openStatementDialog({
                                id: row.members[0]?.id,
                                companyName: row.companyName,
                                firstName: row.members[0]?.firstName,
                                lastName: row.members[0]?.lastName,
                              })}
                              className="text-emerald-600 hover:text-emerald-700"
                              data-testid={`statement-btn-company-${row.companyName.replace(/\s+/g, '-')}`}
                              title="Relevé de compte de l'entreprise (PDF)"
                            >
                              <FileBarChart className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost" size="icon"
                              onClick={() => openWaveRecapDialog({
                                id: row.members[0]?.id,
                                companyName: row.companyName,
                                clientNumber: row.members[0]?.clientNumber,
                                firstName: row.members[0]?.firstName,
                                lastName: row.members[0]?.lastName,
                              })}
                              className="text-amber-600 hover:text-amber-700"
                              data-testid={`wave-recap-btn-company-${row.companyName.replace(/\s+/g, '-')}`}
                              title="Récap Comptable Wave (PDF Premium)"
                            >
                              <Sparkles className="w-4 h-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  }

                  if (row.type === 'member') {
                    const client = row.client;
                    return (
                      <TableRow
                        key={`member-${client.id}`}
                        className="bg-white hover:bg-blue-50/40"
                        data-testid={`client-row-${client.id}`}
                      >
                        <TableCell className="w-10">
                          <div className="ml-2 border-l-2 border-gray-200 h-4"></div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2 pl-2">
                            <User className="w-3.5 h-3.5 text-gray-400 shrink-0" />
                            <div>
                              <div className="flex items-center gap-2 flex-wrap">
                                <p className="text-sm">{client.firstName} {client.lastName}</p>
                                {renderContactsHealthBadge(client)}
                              </div>
                              <p className="text-xs text-gray-400 font-mono">{client.clientNumber || ''}</p>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell className="text-sm">{client.email}</TableCell>
                        <TableCell className="text-sm">{client.phone}</TableCell>
                        <TableCell>
                          <span className="text-xs text-gray-400 italic">entreprise</span>
                        </TableCell>
                        <TableCell>
                          <span className="text-xs text-gray-400 italic">entreprise</span>
                        </TableCell>
                        <TableCell onClick={(e) => e.stopPropagation()}>
                          {renderClientRoleBadge(client)}
                        </TableCell>
                        <TableCell>
                          {client.isActive ? (
                            <span className="inline-flex items-center gap-1.5 text-xs text-gray-700">
                              <span className="w-2 h-2 rounded-full bg-green-500" /> Actif
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1.5 text-xs text-gray-500">
                              <span className="w-2 h-2 rounded-full bg-gray-400" /> Inactif
                            </span>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-1">
                            <Button variant="ghost" size="icon" onClick={() => openCompanyInfoDialog(client)} data-testid={`view-client-${client.id}`} title="Infos entreprise">
                              <Eye className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost" size="icon"
                              onClick={() => openStatementDialog(client)}
                              className="text-emerald-600 hover:text-emerald-700"
                              data-testid={`statement-btn-${client.id}`}
                              title="Relevé de compte (PDF)"
                            >
                              <FileBarChart className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost" size="icon"
                              onClick={() => openWaveRecapDialog(client)}
                              className="text-amber-600 hover:text-amber-700"
                              data-testid={`wave-recap-btn-${client.id}`}
                              title="Récap Comptable Wave (PDF Premium)"
                            >
                              <Sparkles className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost" size="icon"
                              onClick={() => openContactsDialog(client)}
                              className="text-blue-600 hover:text-blue-700"
                              data-testid={`contacts-btn-${client.id}`}
                              title="Contacts & Notifications (multi-personas)"
                            >
                              <Users className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost" size="icon"
                              onClick={() => toggleClientStatus(client.id, client.isActive)}
                              className={client.isActive ? "text-red-600 hover:text-red-700" : "text-green-600 hover:text-green-700"}
                            >
                              {client.isActive ? <Lock className="w-4 h-4" /> : <Unlock className="w-4 h-4" />}
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  }

                  // Individual (particulier)
                  const client = row.client;
                  return (
                    <TableRow
                      key={`individual-${client.id}`}
                      className="hover:bg-gray-50"
                      data-testid={`client-row-${client.id}`}
                    >
                      <TableCell className="w-10">
                        <User className="w-3.5 h-3.5 text-gray-300" />
                      </TableCell>
                      <TableCell>
                        <div>
                          <div className="flex items-center gap-2 flex-wrap">
                            <p className="font-medium text-sm">{client.firstName} {client.lastName}</p>
                            {renderContactsHealthBadge(client)}
                          </div>
                          <p className="text-xs text-gray-400 font-mono">{client.clientNumber || ''}</p>
                        </div>
                      </TableCell>
                      <TableCell className="text-sm">{client.email}</TableCell>
                      <TableCell className="text-sm">{client.phone}</TableCell>
                      <TableCell>{renderSectorBadge({ client })}</TableCell>
                      <TableCell>{renderPaymentBadge({ client })}</TableCell>
                      <TableCell>{renderClientRoleBadge(client)}</TableCell>
                      <TableCell>
                        {client.isActive ? (
                          <span className="inline-flex items-center gap-1.5 text-xs text-gray-700">
                            <span className="w-2 h-2 rounded-full bg-green-500" /> Actif
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1.5 text-xs text-gray-500">
                            <span className="w-2 h-2 rounded-full bg-gray-400" /> Inactif
                          </span>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          <Button variant="ghost" size="icon" onClick={() => openCompanyInfoDialog(client)} data-testid={`view-indiv-${client.id}`} title="Infos client">
                            <Eye className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost" size="icon"
                            onClick={() => openStatementDialog(client)}
                            className="text-emerald-600 hover:text-emerald-700"
                            data-testid={`statement-btn-indiv-${client.id}`}
                            title="Relevé de compte (PDF)"
                          >
                            <FileBarChart className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost" size="icon"
                            onClick={() => openWaveRecapDialog(client)}
                            className="text-amber-600 hover:text-amber-700"
                            data-testid={`wave-recap-btn-indiv-${client.id}`}
                            title="Récap Comptable Wave (PDF Premium)"
                          >
                            <Sparkles className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost" size="icon"
                            onClick={() => openContactsDialog(client)}
                            className="text-blue-600 hover:text-blue-700"
                            data-testid={`contacts-btn-indiv-${client.id}`}
                            title="Contacts & Notifications (multi-personas)"
                          >
                            <Users className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost" size="icon"
                            onClick={() => toggleClientStatus(client.id, client.isActive)}
                            className={client.isActive ? "text-red-600 hover:text-red-700" : "text-green-600 hover:text-green-700"}
                          >
                            {client.isActive ? <Lock className="w-4 h-4" /> : <Unlock className="w-4 h-4" />}
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>

          <TablePagination
            currentPage={currentPage}
            totalItems={groupedRows.length}
            itemsPerPage={itemsPerPage}
            onPageChange={setCurrentPage}
            itemLabel="lignes"
          />
        </CardContent>
      </Card>

      {/* Bulk Actions Bar */}
      <GenericBulkActionsBar
        entityType="clients"
        selectedCount={selectedClients.length}
        onClearSelection={() => { setSelectedClients([]); setSelectAll(false); }}
        onActivate={handleBulkActivate}
        onDeactivate={handleBulkDeactivate}
        onExport={handleBulkExport}
      />

      {/* Payment Policy Dialog */}
      <Dialog open={policyDialogOpen} onOpenChange={setPolicyDialogOpen}>
        <DialogContent className="sm:max-w-md" data-testid="client-payment-policy-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base">
              <CreditCard className="w-5 h-5 text-emerald-600" />
              Condition de paiement par defaut
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="bg-gray-50 rounded-lg p-3 text-sm">
              <span className="text-gray-500">Client : </span>
              <span className="font-semibold">
                {policyDialogTarget?.client?.companyName || `${policyDialogTarget?.client?.firstName || ''} ${policyDialogTarget?.client?.lastName || ''}`}
              </span>
              {policyDialogTarget?.companyName && (
                <p className="text-xs text-blue-600 mt-1">
                  <Building className="w-3 h-3 inline mr-1" />
                  S'applique a tous les membres de l'entreprise
                </p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Modalite de paiement</label>
              <select
                data-testid="client-policy-select"
                value={selectedPolicyId}
                onChange={(e) => setSelectedPolicyId(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 bg-white"
              >
                <option value="">Aucune (défaut système)</option>
                {policies.filter(p => p.is_active !== false).map((p) => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </select>
              <p className="mt-2 text-xs text-gray-500">
                Cette modalite sera pre-sélectionnée lors de la generation de facture pour ce client.
              </p>
            </div>
          </div>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setPolicyDialogOpen(false)} disabled={savingPolicy}>Annuler</Button>
            <Button
              onClick={savePolicyForClient}
              disabled={savingPolicy}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="save-client-policy-btn"
            >
              {savingPolicy ? 'Enregistrement...' : 'Enregistrer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Sector Dialog */}
      <Dialog open={sectorDialogOpen} onOpenChange={setSectorDialogOpen}>
        <DialogContent className="sm:max-w-md" data-testid="client-sector-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base">
              <Briefcase className="w-5 h-5 text-blue-600" />
              Secteur d'activite
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="bg-gray-50 rounded-lg p-3 text-sm">
              <span className="text-gray-500">Client : </span>
              <span className="font-semibold">
                {sectorDialogTarget?.client?.companyName || `${sectorDialogTarget?.client?.firstName || ''} ${sectorDialogTarget?.client?.lastName || ''}`}
              </span>
              {sectorDialogTarget?.companyName && (
                <p className="text-xs text-blue-600 mt-1">
                  <Building className="w-3 h-3 inline mr-1" />
                  S'applique a tous les membres de l'entreprise
                </p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Secteur</label>
              <select
                data-testid="client-sector-select"
                value={selectedSectorCode}
                onChange={(e) => { setSelectedSectorCode(e.target.value); setCustomSectorText(''); }}
                className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
              >
                <option value="">-- Non defini --</option>
                {(() => {
                  const grouped = {};
                  sectors.forEach(s => {
                    if (!grouped[s.category]) grouped[s.category] = [];
                    grouped[s.category].push(s);
                  });
                  return Object.entries(grouped).map(([cat, items]) => (
                    <optgroup key={cat} label={cat}>
                      {items.map(s => (
                        <option key={s.code} value={s.code}>{s.name}</option>
                      ))}
                    </optgroup>
                  ));
                })()}
              </select>
            </div>
            {selectedSectorCode === 'AUTRE' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Preciser le secteur</label>
                <input
                  data-testid="custom-sector-input"
                  type="text"
                  value={customSectorText}
                  onChange={(e) => setCustomSectorText(e.target.value)}
                  placeholder="Ex: Cosmetiques, Evenementiel..."
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            )}
          </div>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setSectorDialogOpen(false)} disabled={savingSector}>Annuler</Button>
            <Button
              onClick={saveSectorForClient}
              disabled={savingSector}
              className="bg-blue-600 hover:bg-blue-700 text-white"
              data-testid="save-client-sector-btn"
            >
              {savingSector ? 'Enregistrement...' : 'Enregistrer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Company Info Dialog */}
      <Dialog open={companyInfoDialogOpen} onOpenChange={setCompanyInfoDialogOpen}>
        <DialogContent className="sm:max-w-md" data-testid="company-info-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base">
              <Building className="w-5 h-5 text-amber-600" />
              Informations entreprise
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="bg-gray-50 rounded-lg p-3 text-sm">
              <span className="text-gray-500">Client : </span>
              <span className="font-semibold">
                {companyInfoTarget?.companyName || `${companyInfoTarget?.firstName || ''} ${companyInfoTarget?.lastName || ''}`}
              </span>
              {companyInfoTarget?.accountType === 'entreprise' && companyInfoTarget?.companyName && (
                <p className="text-xs text-amber-600 mt-1">
                  <Building className="w-3 h-3 inline mr-1" />
                  S'applique a tous les membres de {companyInfoTarget.companyName}
                </p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">RCCM (Registre de Commerce)</label>
              <Input
                data-testid="company-rccm-input"
                value={companyInfoForm.rccm}
                onChange={(e) => setCompanyInfoForm(f => ({ ...f, rccm: e.target.value }))}
                placeholder="Ex: CI-ABJ-2020-B-12345"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">CC (Compte Contribuable)</label>
              <Input
                data-testid="company-cc-input"
                value={companyInfoForm.compteContribuable}
                onChange={(e) => setCompanyInfoForm(f => ({ ...f, compteContribuable: e.target.value }))}
                placeholder="Ex: 1234567 A"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Adresse</label>
              <Input
                data-testid="company-address-input"
                value={companyInfoForm.address}
                onChange={(e) => setCompanyInfoForm(f => ({ ...f, address: e.target.value }))}
                placeholder="Ex: Abidjan, Cocody, Rue des Jardins"
              />
            </div>
          </div>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setCompanyInfoDialogOpen(false)} disabled={savingCompanyInfo}>Annuler</Button>
            <Button
              onClick={saveCompanyInfo}
              disabled={savingCompanyInfo}
              className="bg-amber-600 hover:bg-amber-700 text-white"
              data-testid="save-company-info-btn"
            >
              {savingCompanyInfo ? 'Enregistrement...' : 'Enregistrer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Client Role Dialog */}
      <Dialog open={clientRoleDialogOpen} onOpenChange={setClientRoleDialogOpen}>
        <DialogContent className="sm:max-w-md" data-testid="client-role-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base">
              <Shield className="w-5 h-5 text-purple-600" />
              Sous-rôle client
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="bg-gray-50 rounded-lg p-3 text-sm">
              <span className="text-gray-500">Client : </span>
              <span className="font-semibold">
                {clientRoleTarget?.firstName} {clientRoleTarget?.lastName}
              </span>
              <p className="text-xs text-gray-400 mt-0.5">{clientRoleTarget?.email}</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Sous-rôle</label>
              <select
                data-testid="client-role-select"
                value={selectedClientRole}
                onChange={(e) => setSelectedClientRole(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 bg-white"
              >
                {CLIENT_ROLE_OPTIONS.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
              <div className="mt-3 space-y-1.5 text-xs text-gray-500">
                <p><strong>Administrateur</strong> — Accès complet + gestion des utilisateurs</p>
                <p><strong>Finance</strong> — Accès aux données financières (devis, factures)</p>
                <p><strong>Logistique</strong> — Opérations et suivi, sans données financières</p>
                <p><strong>Auditeur</strong> — Lecture seule sur toutes les données</p>
              </div>
            </div>
          </div>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setClientRoleDialogOpen(false)} disabled={savingClientRole}>Annuler</Button>
            <Button
              onClick={saveClientRole}
              disabled={savingClientRole}
              className="bg-purple-600 hover:bg-purple-700 text-white"
              data-testid="save-client-role-btn"
            >
              {savingClientRole ? 'Enregistrement...' : 'Enregistrer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* CSV Import Modal */}
      <CSVImportModal
        open={showImportModal}
        onOpenChange={setShowImportModal}
        type="clients"
        columns={[
          { key: 'email', label: 'Email' },
          { key: 'firstName', label: 'Prénom' },
          { key: 'lastName', label: 'Nom' },
          { key: 'companyName', label: 'Société' },
          { key: 'phone', label: 'Téléphone' },
          { key: 'accountType', label: 'Type compte' },
        ]}
        onSuccess={() => {
          fetchClients();
          toast({ title: "Import terminé", description: "Les clients ont été importés" });
        }}
        customResultRender={(result) => (
          <div className="space-y-4">
            <div className="text-center">
              <div className="text-2xl font-bold text-[#1db584]">{result.created} client(s) créé(s)</div>
              {result.skipped > 0 && <div className="text-sm text-amber-600">{result.skipped} doublon(s) ignoré(s)</div>}
              {result.errors?.length > 0 && <div className="text-sm text-red-600">{result.errors.length} erreur(s)</div>}
            </div>
            {result.createdUsers?.length > 0 && (
              <div className="border rounded-lg overflow-hidden">
                <div className="bg-amber-50 p-2 text-xs text-amber-700 font-medium">
                  Les mots de passe ci-dessous ne seront plus affichés. Notez-les maintenant.
                </div>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="text-xs">Email</TableHead>
                      <TableHead className="text-xs">Nom</TableHead>
                      <TableHead className="text-xs">Société</TableHead>
                      <TableHead className="text-xs">N° Client</TableHead>
                      <TableHead className="text-xs">Mot de passe</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {result.createdUsers.map((u, i) => (
                      <TableRow key={i}>
                        <TableCell className="text-xs font-mono">{u.email}</TableCell>
                        <TableCell className="text-xs">{u.firstName} {u.lastName}</TableCell>
                        <TableCell className="text-xs">{u.companyName}</TableCell>
                        <TableCell className="text-xs font-mono">{u.clientNumber}</TableCell>
                        <TableCell className="text-xs font-mono font-bold text-red-600">{u.password}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </div>
        )}
      />

      {/* Statement (Relevé de compte) Dialog */}
      <ClientStatementDialog
        open={statementDialogOpen}
        onOpenChange={setStatementDialogOpen}
        client={statementTarget}
      />

      {/* Wave Recap (Récap Comptable Premium) Dialog */}
      <ClientWaveRecapDialog
        open={waveRecapDialogOpen}
        onOpenChange={setWaveRecapDialogOpen}
        client={waveRecapTarget}
      />

      {/* Contacts & Notifications (multi-personas) Dialog */}
      <ClientContactsDialog
        open={contactsDialogOpen}
        onClose={handleContactsDialogClose}
        client={contactsTarget}
      />
    </div>
  );
};

export default ClientsManagement;
